import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, FileDown } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryQuarantineService from '../../../services/nurseryQuarantine';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { downloadBlob } from '../../../utils/downloadBlob';
import { toNum } from '../../../utils/numeric';
import { SPRAY_UNDER_WAY } from '../../../utils/quarantine';
import QuarantineOrderSignoffs, { type SignoffRow } from './QuarantineOrderSignoffs';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz, getTodayInTz } from '../../../utils/timezone';
import { QUARANTINE_CATEGORY_LABEL, QUARANTINE_METHOD_LABEL } from '../../../types/nurseryQuarantine';
import type {
  LoadQuarantine,
  QuarantineCategory,
  QuarantineMethod,
  QuarantineNeed,
  QuarantinePlantsPlan,
  QuarantineTreatment,
  QuarantineTreatmentState,
} from '../../../types/nurseryQuarantine';

/**
 * The load page's Quarantine card (ICA plan, Phase 1).
 *
 * For each protocol the load's orders go out under: the orders going to its
 * states, their plants by tree type and the spray they need, and the
 * treatments made. A treatment is an ordinary spray at the protocol's
 * designated area, made here by a spray creator with the mix locked to the
 * protocol; it is run and signed off on the spray's own page, which records
 * tanks, operators and weather like any spray. Someone other than the
 * operator then validates it here.
 *
 * Renders nothing when no order on the load goes to a protocol state and
 * every order's state is known.
 */

const QUARANTINE_METHODS: QuarantineMethod[] = ['run_off', 'drench'];

const STATE_STYLE: Record<QuarantineTreatmentState, string> = {
  cancelled: 'bg-neutral-100 text-neutral-500',
  awaiting_spray: 'bg-sky-50 text-sky-800',
  awaiting_validation: 'bg-amber-50 text-amber-800',
  treated: 'bg-green-50 text-green-800',
  expired: 'bg-red-50 text-red-800',
};

function plantsText(plan: QuarantinePlantsPlan): string[] {
  return plan.plants_by_tree_type.map((r) => {
    const name = r.tree_type_name ?? 'Unknown tree type';
    const parts = [
      r.to_run_off ? 'sprayed to run-off' : null,
      r.litres_per_plant != null ? `${r.litres_per_plant} L drench per pot = ${r.litres} L` : null,
    ].filter(Boolean);
    return `${r.plants} × ${name}, ${parts.length ? parts.join(' and ') : 'no volume set'}`;
  });
}

function litresText(plan: QuarantinePlantsPlan): string {
  if (plan.planned_litres > 0) {
    return `About ${plan.planned_litres} L of drench mix${plan.run_off_plants ? `, and ${plan.run_off_plants} plants sprayed to run-off` : ''}.`;
  }
  return 'Sprayed to run-off: the tanks say how much went on.';
}

/** A stop's PHAC number, saved when the box is left. */
function PhacField({ loadId, orderId, value, canEdit }: { loadId: number; orderId: number; value: string | null; canEdit: boolean }) {
  const [text, setText] = useState(value ?? '');
  const [saved, setSaved] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setText(value ?? ''); setSaved(value ?? ''); }, [value]);

  if (!canEdit) return value ? <span className="text-neutral-600">{` · PHAC ${value}`}</span> : null;
  const save = async () => {
    if (text.trim() === saved.trim()) return;
    setError(null);
    try {
      const res = await nurseryQuarantineService.setPhac(loadId, orderId, text.trim() || null);
      if (!res.success) { setError(errorMessage(res, 'Not saved')); return; }
      setSaved(text.trim());
    } catch (e) {
      setError(errorMessage(e, 'Not saved'));
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1 ml-1">
      <input aria-label={`PHAC number, order ${orderId}`} className="w-36 border border-neutral-300 rounded px-2 py-0.5 text-sm"
        placeholder="PHAC number" maxLength={40} value={text} onChange={(e) => setText(e.target.value)} onBlur={save} />
      {error && <span className="text-xs text-red-700">{error}</span>}
    </span>
  );
}

/** Crop types with no botanical name: a nursery manager fills it in. */
function SpeciesMissing({ items, canEdit, reload }: {
  items: NonNullable<LoadQuarantine['species_missing']>; canEdit: boolean; reload: () => void;
}) {
  const [names, setNames] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const save = async (id: number) => {
    setError(null);
    try {
      const res = await nurseryQuarantineService.setCropTypeScientificName(id, names[id] ?? '');
      if (!res.success) { setError(errorMessage(res, 'Not saved')); return; }
      reload();
    } catch (e) {
      setError(errorMessage(e, 'Not saved'));
    }
  };
  return (
    <div className="flex items-start gap-2 text-sm text-amber-800">
      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 space-y-2">
        <p>The species sheet cannot name these trees: no genus and species is recorded.</p>
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.crop_type_id ?? m.cultivars.join()} className="break-words">
              {m.crop_type_id == null ? (
                <span>{`${m.cultivars.join(', ')}: no crop type on the cultivar.`}</span>
              ) : (
                <>
                  <span>{`${m.crop_type_name} (${m.cultivars.join(', ')})`}</span>
                  {canEdit && (
                    <span className="flex flex-wrap items-center gap-2 mt-1">
                      <input aria-label={`Genus and species, ${m.crop_type_name}`} placeholder="e.g. Citrus limon"
                        className="w-48 border border-neutral-300 rounded px-2 py-1 text-sm text-neutral-800"
                        value={names[m.crop_type_id] ?? ''}
                        onChange={(e) => setNames({ ...names, [m.crop_type_id as number]: e.target.value })} />
                      <Button variant="outline" size="sm" onClick={() => save(m.crop_type_id as number)}>Save</Button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
        {error && <p className="text-red-700">{error}</p>}
      </div>
    </div>
  );
}

async function download(fetcher: () => Promise<Blob>, filename: string, onError: (m: string) => void) {
  try {
    downloadBlob(await fetcher(), filename);
  } catch (e) {
    onError(errorMessage(e, 'Could not make the PDF'));
  }
}

function TreatmentRow({ t, tz, canValidate, onValidate, busy, onError, code, orders, onChanged }: {
  t: QuarantineTreatment; tz: string; canValidate: boolean; onValidate: () => void; busy: boolean;
  onError: (m: string) => void; code: string; orders: QuarantineNeed['orders']; onChanged: () => void;
}) {
  const [signing, setSigning] = useState<number | null>(null);
  // Each customer order is signed off as treated by whoever applied it (2026-10-14).
  const signs = !!t.order_signoff && t.state !== 'cancelled';
  const rows: SignoffRow[] = t.order_ids.map((id) => {
    const o = orders.find((x) => x.order_id === id);
    const s = (t.signoffs ?? []).find((x) => x.order_id === id);
    return {
      order_id: id, order_number: o?.order_number ?? `Order ${id}`, customer_name: o?.customer_name ?? null,
      gone: !o, signed_at: s?.signed_at ?? null, signed_by_name: s?.signed_by_name ?? null,
    };
  });
  const unsigned = signs ? rows.filter((r) => !r.gone && !r.signed_at).length : 0;
  const change = async (orderId: number, undo: boolean) => {
    setSigning(orderId);
    try {
      const res = undo
        ? await nurseryQuarantineService.undoSignOffOrder(t.id, orderId)
        : await nurseryQuarantineService.signOffOrder(t.id, orderId);
      if (!res.success) { onError(errorMessage(res, 'Could not sign the order off')); return; }
      onChanged();
    } catch (e) {
      onError(errorMessage(e, 'Could not sign the order off'));
    } finally {
      setSigning(null);
    }
  };
  const text: Record<QuarantineTreatmentState, string> = {
    cancelled: 'Spray cancelled: covers nothing',
    awaiting_spray: t.scheduled_date ? `Planned for ${formatLocalDateInTz(t.scheduled_date, tz)}` : 'Planned',
    awaiting_validation: unsigned
      ? `Sprayed. ${unsigned} order${unsigned === 1 ? '' : 's'} still to sign off as treated`
      : 'Sprayed. Waiting for someone other than the operator to validate',
    treated: t.valid_until ? `Treated, valid until ${formatDateTimeInTz(t.valid_until, tz)}` : 'Treated',
    expired: t.valid_until ? `Expired ${formatDateTimeInTz(t.valid_until, tz)}` : 'Expired',
  };
  return (
    <li className="border border-neutral-200 rounded-lg px-3 py-2 space-y-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {t.spray_application_id ? (
          <Link to={`/spray/${t.spray_application_id}`} className="text-sm font-medium text-primary-700 underline">
            {t.reference_number ?? `Spray ${t.spray_application_id}`}
          </Link>
        ) : <span className="text-sm text-neutral-500">Spray removed</span>}
        <span className={cn('text-xs font-medium rounded-full px-2 py-0.5', STATE_STYLE[t.state])}>{text[t.state]}</span>
      </div>
      <p className="text-xs text-neutral-500 break-words">
        {t.method && t.method !== 'all' && `${QUARANTINE_METHOD_LABEL[t.method]} · `}
        {`${t.total_plants} plants, ${t.order_ids.length} order${t.order_ids.length === 1 ? '' : 's'}`}
        {t.area_name && ` · at ${t.area_name}`}
        {t.operator_name && ` · operator ${t.operator_name}`}
        {t.validated_by_name && ` · validated by ${t.validated_by_name}`}
      </p>
      {signs && (
        <QuarantineOrderSignoffs
          rows={rows}
          tz={tz}
          canSign={canValidate && !t.validated_at && SPRAY_UNDER_WAY.includes(t.spray_status ?? '')}
          busyOrderId={signing}
          onSign={(id) => change(id, false)}
          onUndo={(id) => change(id, true)}
        />
      )}
      <div className="flex flex-wrap gap-2">
        {t.state === 'awaiting_validation' && canValidate && unsigned === 0 && (
          <Button variant="outline" size="sm" onClick={onValidate} disabled={busy}>{busy ? 'Validating…' : 'Validate treatment'}</Button>
        )}
        {t.state !== 'cancelled' && (
          <Button variant="outline" size="sm"
            onClick={() => download(() => nurseryQuarantineService.getTreatmentRecordPdf(t.id), `treatment-record-${code}-${t.id}.pdf`, onError)}>
            <FileDown className="h-4 w-4 mr-1" /> Treatment record
          </Button>
        )}
      </div>
    </li>
  );
}

/** What one mix treats now: its orders, plants and chemicals. An older server sends one of everything. */
function forMethod(need: QuarantineNeed, method: QuarantineMethod) {
  return need.untreated_by_method?.[method]
    ?? { order_ids: need.untreated_order_ids, plan: need.untreated_plan, chemicals: need.protocol.chemicals };
}

function NewTreatment({ loadId, need, method, onMade, onCancel }: {
  loadId: number; need: QuarantineNeed; method: QuarantineMethod; onMade: (sprayId: number) => void; onCancel: () => void;
}) {
  const tz = useOrgTimezone();
  const p = need.protocol;
  const todo = forMethod(need, method);
  const chemicals = todo.chemicals;
  const categories = [...new Set(chemicals.map((c) => c.category))] as QuarantineCategory[];
  const [area, setArea] = useState<number | ''>(p.areas.length === 1 ? p.areas[0].location_id : '');
  const [picks, setPicks] = useState<Record<string, number>>(() => Object.fromEntries(
    categories.map((cat) => {
      const opts = chemicals.filter((c) => c.category === cat);
      return [cat, opts.length === 1 ? opts[0].chemical_id : 0];
    })
  ));
  const [date, setDate] = useState(getTodayInTz(tz));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const plan = todo.plan;

  const make = async () => {
    if (area === '') { setError('Choose the designated spray area.'); return; }
    const missing = categories.find((c) => !picks[c]);
    if (missing) { setError(`Choose the ${QUARANTINE_CATEGORY_LABEL[missing].toLowerCase()}.`); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryQuarantineService.createTreatment(loadId, {
        protocol_id: p.id,
        area_location_id: Number(area),
        chemical_ids: Object.values(picks),
        scheduled_date: date,
        notes: notes.trim() || null,
        method,
      });
      if (!res.success || !res.data) { setError(errorMessage(res, 'Could not make the treatment')); return; }
      onMade(res.data.spray_application_id);
    } catch (e) {
      setError(errorMessage(e, 'Could not make the treatment'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-primary-200 rounded-lg p-3 space-y-3">
      <p className="text-sm text-neutral-700 break-words">
        {`${QUARANTINE_METHOD_LABEL[method]}: treats ${todo.order_ids.length} order${todo.order_ids.length === 1 ? '' : 's'}, ${plan.total_plants} plants. ${litresText(plan)}`}
      </p>
      {p.areas.length === 0 ? (
        <p className="text-sm text-amber-700">
          {`${p.code} has no designated spray area at this farm. Add one on `}
          <Link to="/nursery/quarantine" className="underline">Quarantine protocols</Link>.
        </p>
      ) : (
        <div>
          <label htmlFor={`qt-area-${p.id}`} className="block text-sm font-medium text-neutral-700 mb-1">Designated spray area</label>
          <select id={`qt-area-${p.id}`} className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" value={area}
            onChange={(e) => setArea(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">Choose…</option>
            {p.areas.map((a) => <option key={a.location_id} value={a.location_id}>{a.name}</option>)}
          </select>
        </div>
      )}
      {categories.map((cat) => {
        const opts = chemicals.filter((c) => c.category === cat);
        return (
          <fieldset key={cat}>
            <legend className="text-sm font-medium text-neutral-700 mb-1">{QUARANTINE_CATEGORY_LABEL[cat]}</legend>
            <div className="space-y-1">
              {opts.map((c) => (
                <label key={c.chemical_id} className="flex items-start gap-2 text-sm text-neutral-800">
                  <input type="radio" className="mt-1" name={`qt-${p.id}-${method}-${cat}`} checked={picks[cat] === c.chemical_id}
                    onChange={() => setPicks({ ...picks, [cat]: c.chemical_id })} />
                  <span className="min-w-0 break-words">{`${c.chemical_name} ${toNum(c.rate_per_100l)} ${c.rate_unit} per 100 L`}</span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}
      {categories.length === 0 && (
        <p className="text-sm text-amber-700">{`${p.code} has no chemicals for ${QUARANTINE_METHOD_LABEL[method].toLowerCase()}.`}</p>
      )}
      <p className="text-xs text-neutral-500">The chemicals and rates are the protocol's and cannot be changed here.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`qt-date-${p.id}`} className="block text-sm font-medium text-neutral-700 mb-1">Spray on</label>
          <input id={`qt-date-${p.id}`} type="date" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
            value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label htmlFor={`qt-notes-${p.id}`} className="block text-sm font-medium text-neutral-700 mb-1">Notes for the operator</label>
          <input id={`qt-notes-${p.id}`} className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-neutral-500">
        The operator, sprayer and tanks are set on the spray's own page, as for any spray.
      </p>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={make} disabled={busy || p.areas.length === 0}>
          {busy ? 'Making…' : 'Make treatment'}
        </Button>
      </div>
    </div>
  );
}

function NeedBlock({ loadId, need, basis, closed, canCreate, canValidate, canManage, reload }: {
  loadId: number; need: QuarantineNeed; basis: 'picked' | 'ordered'; closed: boolean;
  canCreate: boolean; canValidate: boolean; canManage: boolean; reload: () => void;
}) {
  const tz = useOrgTimezone();
  const [making, setMaking] = useState<QuarantineMethod | null>(null);
  const [made, setMade] = useState<number | null>(null);
  const [validating, setValidating] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = need.protocol;
  const live = need.treatments.filter((t) => t.state !== 'cancelled');
  const allTreated = need.untreated_order_ids.length === 0 && live.length > 0 && live.every((t) => t.state === 'treated');

  const validate = async (t: QuarantineTreatment) => {
    setValidating(t.id);
    setError(null);
    try {
      const res = await nurseryQuarantineService.validateTreatment(t.id);
      if (!res.success) { setError(errorMessage(res, 'Could not validate the treatment')); return; }
      reload();
    } catch (e) {
      setError(errorMessage(e, 'Could not validate the treatment'));
    } finally {
      setValidating(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-neutral-800 break-words">{`${p.code} · ${p.name}`}</h3>
        {allTreated
          ? <span className="text-xs font-medium rounded-full px-2 py-0.5 bg-green-50 text-green-800">All treated</span>
          : need.untreated_order_ids.length > 0 && (
            <span className="text-xs font-medium rounded-full px-2 py-0.5 bg-amber-50 text-amber-800">
              {`${need.untreated_order_ids.length} order${need.untreated_order_ids.length === 1 ? '' : 's'} not treated`}
            </span>
          )}
      </div>
      <ul className="text-sm text-neutral-700 space-y-0.5">
        {need.orders.map((o) => (
          <li key={o.order_id} className="break-words">
            <Link to={`/nursery/orders/${o.order_id}`} className="underline">{o.order_number}</Link>
            {` ${o.customer_name ?? ''} (${o.state}) · ${o.plants} plants`}
            {!o.covered && <span className="text-amber-700"> · not treated</span>}
            <PhacField loadId={loadId} orderId={o.order_id} value={o.phac_number} canEdit={canManage} />
          </li>
        ))}
      </ul>
      <div className="text-sm text-neutral-700">
        <ul className="space-y-0.5">
          {plantsText(need).map((line) => <li key={line} className="break-words">{line}</li>)}
        </ul>
        <p className="mt-1">{litresText(need)}</p>
        <p className="text-xs text-neutral-500 mt-1">
          {basis === 'picked' ? 'Counted from what has been picked.' : 'Counted from what was ordered, until picking starts.'}
        </p>
      </div>
      {need.missing.length > 0 && (
        <p className="flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">
            {`${p.code} has no spray volume for ${need.missing.map((m) => m.tree_type_name).join(', ')}. Set it on `}
            <Link to="/nursery/quarantine" className="underline">Quarantine protocols</Link>.
          </span>
        </p>
      )}

      {need.treatments.length > 0 && (
        <ul className="space-y-2">
          {need.treatments.map((t) => (
            <TreatmentRow key={t.id} t={t} tz={tz} canValidate={canValidate} code={p.code}
              busy={validating === t.id} onValidate={() => validate(t)} onError={setError}
              orders={need.orders} onChanged={reload} />
          ))}
        </ul>
      )}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}

      {made != null && (
        <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Treatment made. <Link to={`/spray/${made}`} className="underline font-medium">Open the spray</Link> to assign the operator and sprayer.
        </p>
      )}
      {!closed && need.untreated_order_ids.length > 0 && (
        canCreate ? (
          making
            ? <NewTreatment loadId={loadId} need={need} method={making} onCancel={() => setMaking(null)}
                onMade={(id) => { setMaking(null); setMade(id); reload(); }} />
            : (
              // The spray to run-off and the soil drench are two mixes, so two treatments.
              <div className="flex flex-wrap gap-2">
                {QUARANTINE_METHODS.filter((m) => forMethod(need, m).order_ids.length > 0).map((m) => (
                  <Button key={m} variant="primary" size="sm" onClick={() => setMaking(m)}>
                    {need.untreated_by_method ? `Make treatment: ${QUARANTINE_METHOD_LABEL[m].toLowerCase()}` : 'Make treatment'}
                  </Button>
                )).slice(0, need.untreated_by_method ? 2 : 1)}
              </div>
            )
        ) : (
          <p className="text-xs text-neutral-500">A Spray Creator seat makes the treatment.</p>
        )
      )}
    </div>
  );
}

export function QuarantineCard({ loadId, loadStatus, refreshKey }: {
  loadId: number; loadStatus: string; refreshKey?: unknown;
}) {
  const { hasAccess: canCreate } = useModuleLicense('spray', ['creator', 'admin']);
  const { hasAccess: canValidate } = useModuleLicense('spray', ['operator', 'creator', 'admin']);
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [data, setData] = useState<LoadQuarantine | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryQuarantineService.getLoadQuarantine(loadId);
      if (res.success) { setData(res.data ?? null); setError(null); }
      else setError(errorMessage(res, 'Could not check the load for quarantine'));
    } catch (e) {
      setError(errorMessage(e, 'Could not check the load for quarantine'));
    }
  }, [loadId]);

  useEffect(() => { load(); }, [load, loadStatus, refreshKey]);

  if (error) {
    return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>;
  }
  if (!data || (data.needs.length === 0 && data.no_state.length === 0)) return null;
  const closed = ['despatched', 'cancelled'].includes(loadStatus);
  const hasLive = data.needs.some((n) => n.treatments.some((t) => t.state !== 'cancelled'));

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4" aria-labelledby="load-quarantine">
      <h2 id="load-quarantine" className="flex items-center gap-2 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
        <ShieldCheck className="h-4 w-4" /> Quarantine
      </h2>
      {data.no_state.length > 0 && (
        <p className="flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">
            {`No state for ${data.no_state.map((o) => `${o.order_number}${o.customer_name ? ` (${o.customer_name})` : ''}`).join(', ')}. `}
            The load cannot be despatched until the delivery address has a state.
          </span>
        </p>
      )}
      {data.needs.map((need) => (
        <NeedBlock key={need.protocol.id} loadId={loadId} need={need} basis={data.count_basis} closed={closed}
          canCreate={canCreate} canValidate={canValidate} canManage={canManage} reload={load} />
      ))}
      {(data.species_missing?.length ?? 0) > 0 && (
        <SpeciesMissing items={data.species_missing ?? []} canEdit={canManage} reload={load} />
      )}
      {data.needs.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-neutral-100">
          {hasLive && (
            <Button variant="outline" size="sm" onClick={() => download(() => nurseryQuarantineService.getQuarantinePackPdf(loadId),
              `quarantine-pack-${data.load.load_number}.pdf`, setPdfError)}>
              <FileDown className="h-4 w-4 mr-1" /> Quarantine pack
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => download(() => nurseryQuarantineService.getSpeciesPdf(loadId),
            `species-${data.load.load_number}.pdf`, setPdfError)}>
            <FileDown className="h-4 w-4 mr-1" /> Species on load
          </Button>
        </div>
      )}
      {pdfError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{pdfError}</div>}
    </section>
  );
}

export default QuarantineCard;
